// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** THE LIVE USGS EARTHQUAKE CAPABILITY — real seismic events worldwide, published alongside
 *  the sealed/ambient tracks and the live ADS-B picture. An SDK-pure capability (mirrors
 *  adsbCapability EXACTLY): it touches NOTHING but HostContext + the contract types + its OWN
 *  data source (usgsSource.ts) — so the dogfood-check SDK-purity scanner and the no-bypass-check
 *  engine-write scanner both stay green.
 *
 *  WHAT IT DOES (the adsbCapability shape exactly):
 *   - On activate it POLLS the USGS GeoJSON summary feed (anonymous REST, NO API KEY,
 *     CORS-permissive) every ≥1min and maps each earthquake feature → a clear-shell `alert`
 *     envelope, then PUBLISHES it via ctx.data.publish. It does NOT paint the map and does NOT
 *     write the engine — the HOST runs each envelope through the PDP and paints the
 *     cleared-visible slice (here: all of it, since USGS data is UNCLASSIFIED). The capability
 *     only contributes a layer + publishes (I13: SDK-publish only, never the engine directly).
 *   - OFFLINE-GRACEFUL: if USGS is unreachable / returns no features, it falls back to a small
 *     BUNDLED snapshot (usgsSource.USGS_SNAPSHOT_RESPONSE) so the capability still publishes in
 *     dev/offline and tests are deterministic. Network errors are caught + logged, NEVER thrown
 *     into the host. A `mode` opt selects live-vs-snapshot.
 *
 *  CLEAR-SHELL ONLY: public earthquake data is UNCLASSIFIED — payload_mode:"clear",
 *  leakage_profile:"clear", NO sealed/encrypted inner. A natural event is affiliation
 *  "neutral" (never hostile, never friendly). */

import type {
  Capability,
  CapabilityManifest,
  Disposable,
  HostContext,
  LayerSpec,
} from "../../../../packages/contracts-ts/src/index.ts";
import {
  USGS_KIND,
  DEFAULT_POLL_MS,
  MIN_POLL_MS,
  fetchLiveQuakes,
  responseToEnvelopes,
  USGS_SNAPSHOT_RESPONSE,
  type UsgsResponse,
} from "./usgsSource.ts";

/** The capability id. */
export const USGS_CAP_ID = "scop:cap:usgs";

/** The deck layer this capability owns — a DISTINCT layer id so it never fights another
 *  capability over the registry, though it draws the shared `alert` kind (the host's per-kind
 *  painter routes cleared-visible alert envelopes to it). */
const LAYER_ID = "scop:layer:usgs";

/** THE MANIFEST — declared BEFORE activation. USGS contributes ONLY a layer (it has no
 *  commands/panels) and requires the (legacy-permissive, UNCLASSIFIED) subscribe permission.
 *  contributes is EXACTLY [layer] so a mutation that widens it goes red in the check. */
export const USGS_MANIFEST: CapabilityManifest = {
  contributes: ["layer"],
  permissions: [
    {
      subject: {},
      action: "subscribe",
      resource: {
        classification: "UNCLASSIFIED",
        releasability: [],
        mission: [],
        caveats: [],
        originator_control: false,
        policy_ref: "policy://defense.local",
        payload_mode: "clear",
        leakage_profile: "clear",
      },
    },
  ],
};

/** The USGS layer spec — a scatter of earthquake alerts, sized per magnitude so larger
 *  events read bigger. */
const USGS_LAYER: LayerSpec = {
  id: LAYER_ID,
  kinds: [USGS_KIND],
  type: "ScatterplotLayer",
  props: { radiusMeters: 12_000 },
  updateTriggers: { getRadius: "magnitude" },
};

/** Live-vs-snapshot switch. "live" polls USGS (falling back to the snapshot on failure);
 *  "snapshot" publishes the bundled fixture ONCE and never touches the network (dev/offline/
 *  deterministic-test). */
export type UsgsMode = "live" | "snapshot";

/** Construction options. */
export interface UsgsCapabilityOptions {
  /** Live polling vs the offline snapshot. Default: env COP_USGS_MODE, else "live". */
  mode?: UsgsMode;
  /** Poll interval (ms) in live mode; floored at MIN_POLL_MS to respect the feed. */
  pollMs?: number;
  /** Injected fetch (tests). Defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Injected logger (tests / host log routing). Defaults to console. */
  logger?: Pick<Console, "warn" | "info">;
  /** Stable capability id (default USGS_CAP_ID). */
  id?: string;
}

/** Resolve the mode: explicit opt > env (COP_USGS_MODE) > "live". */
function resolveMode(opt?: UsgsMode): UsgsMode {
  if (opt) return opt;
  const env = typeof process !== "undefined" ? process.env?.COP_USGS_MODE : undefined;
  return env === "snapshot" ? "snapshot" : "live";
}

/** Build the LIVE USGS earthquake capability. NO host argument, NO PDP — the host's seam does
 *  the clearance filtering (a no-op here, since USGS data is UNCLASSIFIED). PUBLISH-ONLY. */
export function createUsgsCapability(opts: UsgsCapabilityOptions = {}): Capability {
  const id = opts.id ?? USGS_CAP_ID;
  const mode = resolveMode(opts.mode);
  const pollMs = Math.max(opts.pollMs ?? DEFAULT_POLL_MS, MIN_POLL_MS);
  const fetchImpl = opts.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : undefined);
  const log = opts.logger ?? console;

  let disposables: Disposable[] = [];
  let timer: ReturnType<typeof setInterval> | undefined;
  let abort: AbortController | undefined;
  let disposed = false;

  /** Publish a whole USGS response as alert envelopes through the bus. The HOST runs each
   *  through the PDP and paints the cleared-visible slice; we never paint or write the engine. */
  async function publishResponse(ctx: HostContext, resp: UsgsResponse): Promise<void> {
    const envelopes = responseToEnvelopes(resp);
    for (const env of envelopes) {
      if (disposed) return;
      await ctx.data.publish(env);
    }
  }

  /** One LIVE poll: fetch USGS; on ANY failure (network/throttle/malformed) fall back to the
   *  bundled snapshot so the picture never goes blank. Errors are caught in fetchLiveQuakes
   *  (returns null) — this function NEVER throws into the host. */
  async function pollOnce(ctx: HostContext): Promise<void> {
    if (disposed || !fetchImpl) {
      await publishResponse(ctx, USGS_SNAPSHOT_RESPONSE);
      return;
    }
    abort = new AbortController();
    const live = await fetchLiveQuakes(fetchImpl, abort.signal);
    if (disposed) return;
    if (live && (live.features?.length ?? 0) > 0) {
      log.info?.(`[usgs] live poll: ${live.features?.length ?? 0} earthquakes in the past hour`);
      await publishResponse(ctx, live);
    } else {
      log.warn?.("[usgs] feed unreachable/empty — falling back to the bundled snapshot");
      await publishResponse(ctx, USGS_SNAPSHOT_RESPONSE);
    }
  }

  return {
    id,

    async activate(ctx: HostContext): Promise<void> {
      disposed = false;

      // 1. CONTRIBUTE the USGS layer (host-mediated registerLayer). The host creates the GPU
      //    layer and paints the cleared-visible alert envelopes itself — we never touch the
      //    engine's write surface (no-bypass-check stays green).
      disposables = [ctx.registerLayer(USGS_LAYER)];

      // 2. SEED + POLL. Either mode publishes immediately so the map is alive on first paint.
      if (mode === "snapshot") {
        log.info?.("[usgs] snapshot mode — publishing the bundled USGS fixture (offline)");
        await publishResponse(ctx, USGS_SNAPSHOT_RESPONSE);
        return;
      }

      // LIVE: one poll now, then on a conservative interval (≥1min).
      await pollOnce(ctx);
      if (disposed) return;
      timer = setInterval(() => {
        void pollOnce(ctx).catch((err) => log.warn?.(`[usgs] poll error (ignored): ${String(err)}`));
      }, pollMs);
    },

    deactivate(): void {
      disposed = true;
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
      try {
        abort?.abort();
      } catch {
        // best-effort — aborting an in-flight fetch on teardown
      }
      for (const d of disposables.splice(0)) {
        try {
          d.dispose();
        } catch {
          // teardown is best-effort
        }
      }
    },
  };
}
